/**
 * Единственный модуль, знающий форму URL сущностей (ТЗ SEO-8 §6.3, решения Р10/Р11).
 *
 * **Чистые функции, не `@Injectable`**: билдер импортируют `server.ts`,
 * sitemap-билдеры и node-env спеки, где DI-графа нет, а barrel-импорт из
 * `compob2b-libs` притащил бы в vite весь Angular (CLAUDE.md → Testing).
 *
 * **Публичный API принимает сущность целиком, а не ключ** (Р11): если объект уже
 * в памяти, за заголовком не надо ходить на бэк. Плюс заголовок обязателен по
 * типу — с `header?` его легко забыть и молча получить URL без slug.
 */
import {slugify} from 'compob2b-libs/src/lib/seo/slugify';
import {splitUrl} from './legacy-url-map';

/** Сущность, у которой slug строится из `header`, а ключ — числовой `id`. */
interface Keyed {
  id: number;
  header: string;
}

/**
 * То же, но `id` опционален — так описаны `News`, `Promotion` и `TextPage`
 * в `compob2b-libs`.
 *
 * Это артефакт общего интерфейса с create-payload'ом (у новой записи ключа ещё
 * нет), а не свойство данных из листингов. Но притворяться, что ключ есть, нельзя:
 * прежний код клеил строку и выдавал `/cabinet/news/common/undefined`.
 *
 * Обязательность **заголовка** (решение Р11) при этом сохраняется — именно её
 * забыть легко, а ключ приходит из модели, не от вызывающего.
 */
interface MaybeKeyed {
  id?: number | null;
  header: string;
}

/**
 * Товар — особый случай (ТЗ §11.6): ключ в хвосте это `syncUid`, а не `id`
 * (`/product/{uid}` на бэке резолвится только через `getBySyncUid`), плюс есть
 * хранимый slug `url`.
 */
interface ProductLike {
  syncUid: string;
  header: string;
  /**
   * Хранимый на бэке slug. Типизирован в `ProductCustomer` как `string`, но в
   * проде приходит `null` — **тип врёт**, проверка обязана быть runtime.
   */
  url?: string | null;
}

// News и Promotion — с опциональным ключом (см. MaybeKeyed). При отсутствии ключа
// отдаём URL СПИСКА: ссылка на раздел — честная деградация, `/news/undefined` — нет.
export const newsUrl = (news: MaybeKeyed): string =>
  news.id == null ? '/news' : entityPath('news', news.id, news.header);
export const promotionUrl = (p: MaybeKeyed): string =>
  p.id == null ? '/promotions' : entityPath('promotions', p.id, p.header);
export const pageUrl = (p: MaybeKeyed): string =>
  p.id == null ? '/pages' : entityPath('pages', p.id, p.header);
export const faqUrl = (f: Keyed): string => entityPath('faq', f.id, f.header);
export const catalogUrl = (c: Keyed): string => entityPath('catalog', c.id, c.header);

/**
 * Товар. Если `url` непустой — он и есть slug, генерируемый сегмент НЕ
 * добавляется, иначе вышло бы `/product/drel-bosch/drel-bosch-gsb-13`.
 *
 * Побочная выгода: когда бэк начнёт заполнять `url`, URL сам собой станет
 * чистым `/product/{url}`, а резолвер переведёт старую форму на новую 301-м,
 * потому что сравнивает фактический путь с результатом этой функции.
 */
export function productUrl(product: ProductLike): string {
  if (product.url) {
    return `/product/${encodeURIComponent(product.url)}`;
  }
  return entityPath('product', product.syncUid, product.header);
}

/**
 * Страница аналогов товара. Строится ОТ канонической формы карточки, а не
 * отдельной сборкой строки: иначе появилась бы вторая реализация формы URL и
 * резолвер §6.4 сравнивал бы несравнимое.
 */
export function productAnalogsUrl(product: ProductLike): string {
  return `${productUrl(product)}/analogs`;
}

/**
 * Обратное чтение: id категории из пути (ТЗ §7).
 *
 * Появилась потому, что после переезда `catalog_id` из query в path **компоненты
 * вне каталожного роута** теряют к нему доступ: `filter-component` и
 * `search-autocomplete` живут в лейауте, читали `route.queryParamMap` (query виден
 * из любого роута) и по path-параметру каталога достать ничего не могут.
 *
 * Ревизия 8 вырезала спекулятивный `parseEntityPath` как YAGNI — вот и настоящий
 * потребитель, ради которого чтение формы URL остаётся в билдере, а не
 * расползается регулярками по компонентам.
 *
 * @param url путь навигации, возможно с query/hash (`router.url`)
 * @returns числовой id категории либо `null` для корня каталога и чужих путей
 */
export function catalogIdFromPath(url: string): number | null {
  // Разбор — общим `splitUrl` (`legacy-url-map.ts`), а не своим `split('#')/split('?')`:
  // в этом слое он объявлен единственным разборщиком URL, и вторая копия правил
  // отделения query/hash разъехалась бы молча. Цикла нет: `legacy-url-map` ничего
  // не импортирует вовсе.
  const segments = splitUrl(url).pathname.split('/').filter(Boolean);

  // `/catalog` — весь каталог, это НЕ категория 0: ноль уехал бы в API как
  // осмысленный идентификатор.
  if (segments[0] !== 'catalog' || segments.length < 2) {
    return null;
  }

  // Ключ — последний сегмент в обеих формах (`/catalog/{id}` и `/catalog/{slug}/{id}`).
  const key = segments[segments.length - 1];
  // Строго числовой: иначе это чужой URL, а не «категория abc».
  return /^\d+$/.test(key) ? Number(key) : null;
}

/**
 * Разделы, у которых URL несёт slug (карта ТЗ §4). **Единственный источник списка.**
 *
 * Массив, а не только union: список нужен в рантайме (`redirect-status.ts` решает,
 * поднимать ли 302→301). Раньше он был продублирован там строкой-массивом без
 * какой-либо проверки — добавив седьмой раздел, можно было получить роуты и
 * резолвер (их `Record<EntityKind, …>` заставляет компилятор), но молча остаться
 * на 302, который Google не консолидирует.
 */
export const ENTITY_KINDS = ['news', 'promotions', 'pages', 'faq', 'catalog', 'product', 'list'] as const;

export type EntityKind = typeof ENTITY_KINDS[number];

/**
 * Универсальная сборка по виду сущности — для резолвера §6.4, которому нужен один
 * вход на все разделы (`ENTITY_KINDS`).
 *
 * Реализована через те же `entityPath`/`productUrl`, а не отдельной таблицей
 * префиксов: иначе появилась бы вторая реализация формы URL, и резолвер сравнивал бы
 * фактический путь с чужой сборкой.
 */
export function entityUrlByKind(
  kind: EntityKind,
  entity: {key: string | number; header: string; storedUrl?: string | null},
): string {
  if (kind === 'product') {
    return productUrl({syncUid: String(entity.key), header: entity.header, url: entity.storedUrl});
  }
  return entityPath(kind, entity.key, entity.header);
}

/**
 * Адрес SEO-подборки. Сборка та же, что у остальных разделов: раздел + слаг + ключ.
 * Отдельная функция — чтобы компоненты звали её по имени, как `newsUrl`/`catalogUrl`.
 */
export function listPageUrl(page: {id: string | number; header: string}): string {
  return entityPath('list', page.id, page.header);
}

/**
 * Escape hatch: путь по одному ключу, без slug (ТЗ §6.3). Имя намеренно
 * говорящее — вызов должен быть заметен на ревью.
 *
 * Легальный потребитель остался ОДИН — ссылка на оферту в футере лейаута: её id
 * зашит константой, объекта страницы в памяти нет вовсе. Каждый следующий вызов —
 * повод спросить «почему объекта нет»: на ревизии 9 так нашлись два обхода
 * билдера, оба чинились передачей объекта (навигация по дереву каталога в
 * `modal-catalog` и крошки карточки в `modal-product` — там заголовок был под
 * рукой, а URL строился по одному id и получал 301 в ответ).
 *
 * Реализован через ту же `entityPath`, а не отдельной сборкой строки: иначе
 * появилась бы вторая реализация формы URL, и резолвер §6.4 сравнивал бы
 * несравнимое.
 */
export function entityPathByKey(kind: EntityKind, key: string | number): string {
  return entityPath(kind, key, '');
}

/**
 * Общая сборка `/{раздел}/{slug}/{ключ}`. Пустой slug — валидный случай: путь
 * вырождается в `/{раздел}/{ключ}`, для чего у каждой сущности объявлен второй
 * роут `:key` (ТЗ §5.4).
 */
function entityPath(section: string, key: string | number, header: string): string {
  const slug = slugify(header);
  // Регистр ключа НЕ трогаем. Ключ уезжает на бэк (`getBySyncUid`) и обязан
  // сохраняться дословно: резолвер (§6.4) сравнивает фактический путь с
  // результатом этой функции, поэтому «нормализованный» ключ увёл бы 301-м на
  // URL, который бэк не разрешит. Нижний регистр — свойство slug, не ключа.
  const encodedKey = encodeURIComponent(String(key));
  return slug ? `/${section}/${slug}/${encodedKey}` : `/${section}/${encodedKey}`;
}
